import React, { useState, useEffect, useCallback } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Box,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from "@material-ui/core";
import RefreshIcon from "@material-ui/icons/Refresh";
import VisibilityIcon from "@material-ui/icons/Visibility";
import OpenInNewIcon from "@material-ui/icons/OpenInNew";
import { useHistory } from "react-router-dom";
import moment from "moment";
import {
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import api from "../../services/api";
import { toast } from "react-toastify";

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(2),
    overflowY: "scroll",
    ...theme.scrollbarStyles,
  },
  filters: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(2),
    alignItems: "center",
    marginBottom: theme.spacing(2),
  },
  metricCard: { height: "100%" },
  metricValue: { fontSize: "1.8rem", fontWeight: 700 },
  metricLabel: { color: theme.palette.text.secondary, fontSize: "0.85rem" },
  chartBox: { width: "100%", height: 280 },
  sectionTitle: { margin: theme.spacing(2, 0, 1, 0), fontWeight: 600 },
  funnelRow: { marginBottom: theme.spacing(1) },
  nodeChip: { marginRight: 4 },
}));

const STATUS_LABEL = {
  completed: "Concluído",
  error: "Erro",
  waiting_input: "Aguardando resposta",
  in_progress: "Em andamento",
};
const STATUS_COLOR = {
  completed: "#2e7d32",
  error: "#c62828",
  waiting_input: "#ef6c00",
  in_progress: "#1565c0",
};
const PIE_COLORS = ["#c62828", "#ad1457", "#6a1b9a", "#283593", "#00695c", "#ef6c00", "#4e342e"];

const fmtDuration = (sec) => {
  if (!sec && sec !== 0) return "-";
  const s = Math.round(sec);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}m ${r}s`;
};

const MetricCard = ({ label, value, suffix }) => {
  const classes = useStyles();
  return (
    <Card className={classes.metricCard}>
      <CardContent>
        <Typography className={classes.metricValue}>
          {value}
          {suffix || ""}
        </Typography>
        <Typography className={classes.metricLabel}>{label}</Typography>
      </CardContent>
    </Card>
  );
};

const FlowAnalytics = () => {
  const classes = useStyles();
  const history = useHistory();

  const [loading, setLoading] = useState(false);
  const [flowId, setFlowId] = useState("");
  const [status, setStatus] = useState("");
  const [startDate, setStartDate] = useState(
    moment().subtract(7, "days").format("YYYY-MM-DD")
  );
  const [endDate, setEndDate] = useState(moment().format("YYYY-MM-DD"));

  const [overview, setOverview] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [byFlow, setByFlow] = useState([]);
  const [funnel, setFunnel] = useState([]);
  const [errorsData, setErrorsData] = useState({ byReason: [], recent: [] });

  const [executions, setExecutions] = useState([]);
  const [execTotal, setExecTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);

  const [detail, setDetail] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);

  const commonParams = useCallback(() => {
    const params = {};
    if (flowId) params.flowId = flowId;
    if (startDate) params.startDate = `${startDate} 00:00:00`;
    if (endDate) params.endDate = `${endDate} 23:59:59`;
    return params;
  }, [flowId, startDate, endDate]);

  const loadAggregates = useCallback(async () => {
    setLoading(true);
    try {
      const params = commonParams();
      const [ov, tl, bf, fn, er] = await Promise.all([
        api.get("/flow-analytics/overview", { params }),
        api.get("/flow-analytics/timeline", { params }),
        api.get("/flow-analytics/by-flow", { params }),
        api.get("/flow-analytics/funnel", { params }),
        api.get("/flow-analytics/errors", { params }),
      ]);
      setOverview(ov.data);
      setTimeline(tl.data || []);
      setByFlow(bf.data || []);
      setFunnel(fn.data || []);
      setErrorsData(er.data || { byReason: [], recent: [] });
    } catch (err) {
      toast.error("Erro ao carregar analytics do fluxo");
    } finally {
      setLoading(false);
    }
  }, [commonParams]);

  const loadExecutions = useCallback(async () => {
    try {
      const params = { ...commonParams(), page: page + 1, pageSize };
      if (status) params.status = status;
      const { data } = await api.get("/flow-analytics/executions", { params });
      setExecutions(data.records || []);
      setExecTotal(data.total || 0);
    } catch (err) {
      toast.error("Erro ao carregar execuções");
    }
  }, [commonParams, page, pageSize, status]);

  useEffect(() => {
    loadAggregates();
  }, [loadAggregates]);

  useEffect(() => {
    loadExecutions();
  }, [loadExecutions]);

  const openDetail = async (id) => {
    try {
      const { data } = await api.get(`/flow-analytics/executions/${id}`);
      setDetail(data);
      setDetailOpen(true);
    } catch (err) {
      toast.error("Erro ao carregar detalhe da execução");
    }
  };

  const openTicket = (exec) => {
    if (!exec || !exec.ticketId) {
      toast.info("Esta execução não está vinculada a um ticket.");
      return;
    }
    // Mesmo padrão do painel: navega pelo uuid do ticket (fallback para o id).
    history.push(`/tickets/${exec.ticketUuid || exec.ticketId}`);
  };

  const maxFunnel = funnel.length ? funnel[0].executions : 0;

  return (
    <MainContainer>
      <MainHeader>
        <Title>Auditoria de Fluxos</Title>
        <Box flexGrow={1} />
        <IconButton onClick={loadAggregates} disabled={loading}>
          <RefreshIcon />
        </IconButton>
      </MainHeader>

      <Paper className={classes.mainPaper} variant="outlined">
        {/* Filtros */}
        <div className={classes.filters}>
          <FormControl variant="outlined" size="small" style={{ minWidth: 220 }}>
            <InputLabel>Fluxo</InputLabel>
            <Select
              label="Fluxo"
              value={flowId}
              onChange={(e) => setFlowId(e.target.value)}
            >
              <MenuItem value="">Todos os fluxos</MenuItem>
              {byFlow.map((f) => (
                <MenuItem key={f.flowId} value={f.flowId}>
                  {f.flowName} (#{f.flowId})
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl variant="outlined" size="small" style={{ minWidth: 180 }}>
            <InputLabel>Status</InputLabel>
            <Select
              label="Status"
              value={status}
              onChange={(e) => {
                setStatus(e.target.value);
                setPage(0);
              }}
            >
              <MenuItem value="">Todos</MenuItem>
              {Object.keys(STATUS_LABEL).map((s) => (
                <MenuItem key={s} value={s}>
                  {STATUS_LABEL[s]}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            label="Início"
            type="date"
            size="small"
            variant="outlined"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="Fim"
            type="date"
            size="small"
            variant="outlined"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
        </div>

        {loading && <LinearProgress />}

        {/* Métricas */}
        {overview && (
          <Grid container spacing={2}>
            <Grid item xs={6} sm={4} md={2}>
              <MetricCard label="Execuções" value={overview.total} />
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <MetricCard label="Taxa de conclusão" value={overview.completionRate} suffix="%" />
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <MetricCard label="Taxa de erro" value={overview.errorRate} suffix="%" />
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <MetricCard label="Aguardando" value={overview.waitingInput} />
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <MetricCard label="Média de nós" value={overview.avgNodesExecuted} />
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <MetricCard label="Duração média" value={fmtDuration(overview.avgDurationSeconds)} />
            </Grid>
          </Grid>
        )}

        {/* Timeline */}
        <Typography className={classes.sectionTitle}>Execuções ao longo do tempo</Typography>
        <div className={classes.chartBox}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={timeline}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="day" />
              <YAxis allowDecimals={false} />
              <RechartsTooltip />
              <Legend />
              <Line type="monotone" dataKey="total" name="Total" stroke="#1565c0" strokeWidth={2} />
              <Line type="monotone" dataKey="completed" name="Concluídos" stroke="#2e7d32" strokeWidth={2} />
              <Line type="monotone" dataKey="error" name="Erros" stroke="#c62828" strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        </div>

        <Grid container spacing={2}>
          {/* Por fluxo */}
          <Grid item xs={12} md={6}>
            <Typography className={classes.sectionTitle}>Por fluxo</Typography>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Fluxo</TableCell>
                    <TableCell align="right">Total</TableCell>
                    <TableCell align="right">Concluídos</TableCell>
                    <TableCell align="right">Erros</TableCell>
                    <TableCell align="right">% Conclusão</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {byFlow.map((f) => (
                    <TableRow key={f.flowId} hover>
                      <TableCell>{f.flowName} <small>#{f.flowId}</small></TableCell>
                      <TableCell align="right">{f.total}</TableCell>
                      <TableCell align="right">{f.completed}</TableCell>
                      <TableCell align="right">{f.error}</TableCell>
                      <TableCell align="right">{f.completionRate}%</TableCell>
                    </TableRow>
                  ))}
                  {byFlow.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5} align="center">Sem dados no período</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>

          {/* Erros */}
          <Grid item xs={12} md={6}>
            <Typography className={classes.sectionTitle}>Erros por motivo</Typography>
            {errorsData.byReason.length > 0 ? (
              <div className={classes.chartBox}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={errorsData.byReason}
                      dataKey="count"
                      nameKey="reason"
                      cx="50%"
                      cy="50%"
                      outerRadius={90}
                      label={(e) => `${e.reason}: ${e.count}`}
                    >
                      {errorsData.byReason.map((entry, i) => (
                        <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                      ))}
                    </Pie>
                    <RechartsTooltip />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <Typography variant="body2" color="textSecondary">
                Nenhum erro registrado no período. 🎉
              </Typography>
            )}
          </Grid>
        </Grid>

        {/* Funil de nós */}
        <Typography className={classes.sectionTitle}>
          Funil de nós {flowId ? "" : "(selecione um fluxo para um funil preciso)"}
        </Typography>
        {funnel.slice(0, 25).map((n) => {
          const pct = maxFunnel ? Math.round((n.executions / maxFunnel) * 100) : 0;
          return (
            <div key={n.nodeId} className={classes.funnelRow}>
              <Box display="flex" justifyContent="space-between">
                <span>
                  <Chip size="small" label={n.nodeType || "?"} className={classes.nodeChip} />
                  <small>{n.nodeId}</small>
                </span>
                <small>{n.executions} execuções</small>
              </Box>
              <LinearProgress variant="determinate" value={pct} />
            </div>
          );
        })}
        {funnel.length === 0 && (
          <Typography variant="body2" color="textSecondary">Sem nós executados no período.</Typography>
        )}

        <Divider style={{ margin: "16px 0" }} />

        {/* Execuções */}
        <Typography className={classes.sectionTitle}>Execuções</Typography>
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Fluxo</TableCell>
                <TableCell>Ticket</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Nós</TableCell>
                <TableCell>Duração</TableCell>
                <TableCell>Motivo / Erro</TableCell>
                <TableCell>Início</TableCell>
                <TableCell align="center">Detalhe</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {executions.map((e) => (
                <TableRow key={e.id} hover>
                  <TableCell>{e.id}</TableCell>
                  <TableCell>{e.flowName} <small>#{e.flowId}</small></TableCell>
                  <TableCell>{e.ticketId || "-"}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={STATUS_LABEL[e.status] || e.status}
                      style={{ backgroundColor: STATUS_COLOR[e.status] || "#777", color: "#fff" }}
                    />
                  </TableCell>
                  <TableCell align="right">{e.nodesExecuted}</TableCell>
                  <TableCell>{fmtDuration(e.durationSeconds)}</TableCell>
                  <TableCell>
                    <Tooltip title={e.errorMessage || e.exitReason || ""}>
                      <span>{e.errorMessage || e.exitReason || "-"}</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell>{moment(e.createdAt).format("DD/MM/YY HH:mm")}</TableCell>
                  <TableCell align="center">
                    <Tooltip title="Ver detalhes da execução">
                      <IconButton size="small" onClick={() => openDetail(e.id)}>
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Abrir atendimento (ticket)">
                      <span>
                        <IconButton
                          size="small"
                          onClick={() => openTicket(e)}
                          disabled={!e.ticketId}
                        >
                          <OpenInNewIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
              {executions.length === 0 && (
                <TableRow>
                  <TableCell colSpan={9} align="center">Nenhuma execução no período/filtro.</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={execTotal}
            page={page}
            onPageChange={(e, p) => setPage(p)}
            rowsPerPage={pageSize}
            onRowsPerPageChange={(e) => {
              setPageSize(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[10, 20, 50, 100]}
          />
        </TableContainer>
      </Paper>

      {/* Dialog de detalhe */}
      <Dialog open={detailOpen} onClose={() => setDetailOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Execução #{detail?.id}</DialogTitle>
        <DialogContent dividers>
          {detail && (
            <>
              <Grid container spacing={1} style={{ marginBottom: 12 }}>
                <Grid item xs={6} sm={3}><b>Fluxo:</b> #{detail.flowId}</Grid>
                <Grid item xs={6} sm={3}><b>Ticket:</b> {detail.ticketId || "-"}</Grid>
                <Grid item xs={6} sm={3}>
                  <b>Status:</b>{" "}
                  <Chip
                    size="small"
                    label={STATUS_LABEL[detail.status] || detail.status}
                    style={{ backgroundColor: STATUS_COLOR[detail.status] || "#777", color: "#fff" }}
                  />
                </Grid>
                <Grid item xs={6} sm={3}><b>Gatilho:</b> {detail.triggerType || "-"}</Grid>
                <Grid item xs={12} sm={6}><b>Início:</b> {moment(detail.createdAt).format("DD/MM/YYYY HH:mm:ss")}</Grid>
                <Grid item xs={12} sm={6}>
                  <b>Fim:</b> {detail.finishedAt ? moment(detail.finishedAt).format("DD/MM/YYYY HH:mm:ss") : "-"}
                </Grid>
                {detail.exitReason && (
                  <Grid item xs={12}><b>Motivo de saída:</b> {detail.exitReason}</Grid>
                )}
                {detail.errorMessage && (
                  <Grid item xs={12}><b>Erro:</b> <span style={{ color: "#c62828" }}>{detail.errorMessage}</span></Grid>
                )}
              </Grid>

              <Typography className={classes.sectionTitle}>
                Nós percorridos ({(detail.nodes || []).length})
              </Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>#</TableCell>
                      <TableCell>Tipo</TableCell>
                      <TableCell>Node ID</TableCell>
                      <TableCell>Quando</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(detail.nodes || []).map((n) => (
                      <TableRow key={n.id}>
                        <TableCell>{n.nodeOrder}</TableCell>
                        <TableCell><Chip size="small" label={n.nodeType || "?"} /></TableCell>
                        <TableCell><small>{n.nodeId}</small></TableCell>
                        <TableCell>{moment(n.createdAt).format("HH:mm:ss")}</TableCell>
                      </TableRow>
                    ))}
                    {(detail.nodes || []).length === 0 && (
                      <TableRow>
                        <TableCell colSpan={4} align="center">Sem nós registrados.</TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </DialogContent>
        <DialogActions>
          {detail?.ticketId && (
            <Button
              onClick={() => openTicket(detail)}
              color="primary"
              startIcon={<OpenInNewIcon />}
            >
              Abrir ticket #{detail.ticketId}
            </Button>
          )}
          <Button onClick={() => setDetailOpen(false)} color="primary">Fechar</Button>
        </DialogActions>
      </Dialog>
    </MainContainer>
  );
};

export default FlowAnalytics;
